import { TemplateResult, html, LitElement } from 'lit';
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import innerStyles from './styles/outline-video.inner.pcss?inline';
import globalStyles from './styles/outline-video.global.pcss?inline';
import { SlotHelper } from '../../controllers/slot-helper';
import '../outline-accordion-item/outline-accordion-item';
import '../outline-heading/outline-heading';
import '../outline-svg/outline-svg';
import Player from '@vimeo/player';

/**
 * The OutlineVideo component
 * @element outline-video
 * @slot default - for iframe/video
 * @slot thumbnail - for thumbnail image
 * @slot transcript - for outline-ckeditor-wrapped transcript text
 * @slot caption - for video caption
 */
@customElement('outline-video')
export class OutlineVideo extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  private _slotHelper = new SlotHelper(this);

  @property({ type: Boolean, attribute: 'has-caption' })
  hasCaption: boolean;

  @property({ type: Boolean, attribute: 'has-transcript' })
  hasTranscript: boolean;

  @property({ type: Boolean, attribute: 'has-thumbnail' })
  hasThumbnail: boolean;

  @property({ type: Boolean, attribute: 'has-chips' })
  hasChips: boolean;

  @property({ type: Boolean, attribute: 'has-link' })
  hasTransLink: boolean;

  @property({ type: Boolean, reflect: true })
  playing = false;

  @property({ type: Boolean, attribute: 'transcript-open' })
  transcriptOpen = false;

  @queryAssignedElements()
  videoRef: Array<HTMLIFrameElement>;

  private _originalSrc: string | null = null;

  private vimeoPlayer: Player | null = null;

  private _handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      // Page became visible - reset if we were playing
      if (this.playing) {
        this.resetPlayer();
      }
    }
  };

  connectedCallback() {
    super.connectedCallback();
    // Reset player state when we reconnect to DOM
    this.resetPlayer();

    // Listen for page visibility changes
    document.addEventListener('visibilitychange', this._handleVisibilityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.playing = false;
    document.removeEventListener(
      'visibilitychange',
      this._handleVisibilityChange
    );
  }

  render(): TemplateResult {
    this.hasCaption = this._slotHelper.isNotEmpty('caption');
    this.hasTranscript = this._slotHelper.isNotEmpty('transcript');
    this.hasThumbnail = this._slotHelper.isNotEmpty('thumbnail');
    this.hasChips = this._slotHelper.isNotEmpty('chips');
    this.hasTransLink = this._slotHelper.isNotEmpty('transcript-link');

    return html`
      <section class="outline-video">
        ${this.figureTemplate()} ${this.transcriptTemplate()}
        ${this.chipsTemplate()}
      </section>
    `;
  }

  buttonTemplate() {
    return html`
      <button
        class="outline-video--button"
        @click="${this.handleButton}"
        aria-label="play video"
      ></button>
      <span
        class="outline-video--button-offset"
        @click="${this.handleButton}"
      ></span>
    `;
  }

  figureTemplate() {
    if (this.hasCaption) {
      return html`
        <figure class="video-figure">
          <div class="video-wrapper">
            ${this.buttonTemplate()}
            <slot></slot>
            ${this.thumbnailTemplate()}
          </div>
          <figcaption class="video-caption">
            <slot name="caption"></slot>
          </figcaption>
        </figure>
      `;
    } else {
      return html`
        <div class="video-wrapper">
          ${this.buttonTemplate()}
          <slot></slot>
          ${this.thumbnailTemplate()}
        </div>
      `;
    }
  }

  thumbnailTemplate() {
    if (this.hasThumbnail) {
      return html`<slot name="thumbnail"></slot>`;
    } else {
      return html`
        <div
          class="missing-thumbnail"
          ?hidden="${this.playing}"
          role="button"
          tabindex="0"
          @click="${this.handleButton}"
          @keydown="${(e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.handleButton(e);
            }
          }}"
          aria-label="${this.playing ? 'Pause video' : 'Play video'}"
        ></div>
      `;
    }
  }

  transcriptTemplate() {
    if (this.hasTransLink) {
      return html`
        <div class="video-transcript-link">
          <slot name="transcript-link"></slot>
        </div>
      `;
    } else if (this.hasTranscript) {
      return html`
        <div class="video-transcript">
          <outline-accordion-item @click=${this.transcriptOpenHandler}>
            <outline-heading slot="summary" heading-size="sm" id="summary">
              <h3>
                ${this.transcriptOpen
                  ? 'Collapse video transcript'
                  : 'Expand video transcript'}
              </h3>
            </outline-heading>
            <slot name="transcript"></slot>
          </outline-accordion-item>
        </div>
      `;
    } else {
      return;
    }
  }

  transcriptOpenHandler(e: Event) {
    const allowedTargets = [
      'OUTLINE-ACCORDION-ITEM',
      'OUTLINE-HEADING',
      'H3',
      'H4',
    ];
    if (allowedTargets.includes((e.target as HTMLElement).nodeName)) {
      this.transcriptOpen = !this.transcriptOpen;
    }
  }

  chipsTemplate() {
    if (this.hasChips) {
      return html` <div class="chips-wrapper">
        <slot name="chips"></slot>
      </div>`;
    } else {
      return;
    }
  }

  handleButton(e: Event) {
    e.preventDefault();

    if (this.videoRef && this.videoRef.length > 0) {
      const iframe = this.videoRef[0];

      if (iframe.src && this._originalSrc !== iframe.src) {
        this._originalSrc = iframe.src;
        if (this.vimeoPlayer && (this.vimeoPlayer as unknown as { element?: HTMLIFrameElement }).element !== iframe) {
          try {
            this.vimeoPlayer.unload?.();
          } catch (err) {
          }
          this.vimeoPlayer = null;
        }
      }

      // Add missing allow attributes
      const currentAllow = iframe.getAttribute('allow') ?? '';
      if (iframe.src.includes('youtube.com')) {
        if (
          !currentAllow.includes('autoplay') ||
          !currentAllow.includes('encrypted-media')
        ) {
          iframe.setAttribute('allow', 'autoplay; encrypted-media');
        }
      } else if (iframe.src.includes('vimeo.com')) {
        if (
          !currentAllow.includes('autoplay') ||
          !currentAllow.includes('fullscreen')
        ) {
          iframe.setAttribute(
            'allow',
            'autoplay; fullscreen; picture-in-picture'
          );
        }
      }

      if (iframe.src.includes('vimeo.com')) {
        // Handle Vimeo via @vimeo/player
        if (!this.vimeoPlayer) {
          this.vimeoPlayer = new Player(iframe);
        }
        this.vimeoPlayer.play();
      } else {
        // Handle Vimeo via enablejsapi
        if (!this._originalSrc) {
          this._originalSrc = iframe.src;
        }

        let srcWithApi = '';
        if (this._originalSrc.includes('enablejsapi=1')) {
          srcWithApi = this._originalSrc;
        } else if (this._originalSrc.includes('?')) {
          srcWithApi = this._originalSrc + '&enablejsapi=1';
        } else {
          srcWithApi = this._originalSrc + '?enablejsapi=1';
        }

        if (!iframe.src.includes('enablejsapi=1')) {
          iframe.onload = () => {
            this.sendPlayCommand(iframe);
          };
          iframe.src = srcWithApi;
        } else {
          this.sendPlayCommand(iframe);
        }
      }

      this.playing = true;
    }

    this.requestUpdate();
  }

  private sendPlayCommand(iframe: HTMLIFrameElement) {
    iframe.contentWindow?.postMessage(
      JSON.stringify({
        event: 'command',
        func: 'playVideo',
        args: [],
      }),
      '*'
    );
  }

  resetPlayer() {
    this.playing = false;

    if (this.videoRef && this.videoRef.length > 0) {
      const iframe = this.videoRef[0];

      if (this._originalSrc) {
        // Reset to original url
        iframe.src = this._originalSrc;
      } else {
        // Force reload by temporarily clearing and resetting src
        const currentSrc = iframe.src;
        if (currentSrc) {
          // Remove autoplay param, if present
          const cleanSrc = currentSrc.split(/[?&]autoplay=1/)[0];
          iframe.src = '';
          // confirm the clearing takes effect
          requestAnimationFrame(() => {
            iframe.src = cleanSrc;
            this._originalSrc = cleanSrc;
          });
        }
      }
    }

    this.hasThumbnail = this._slotHelper.isNotEmpty('thumbnail');

    // Force an update to keep ui in sync.
    this.requestUpdate();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-video': OutlineVideo;
  }
}
